// @ts-nocheck
import { useState, useRef, useCallback, useEffect } from 'react';
import {
  List,
  X,
  Shield,
  GitBranch,
  Code,
  PlugsConnected,
  Buildings,
  CaretDown,
  Terminal,
  Rocket,
  FileText,
  BookOpen,
  DownloadSimple,
  GraduationCap,
  Scroll,
  HardDrives,
  Article,
  ListChecks,
} from '@phosphor-icons/react';
import { BLOG_PATH, SIGNUP_URL } from '../lib/appUrls';
import { useLocation } from 'react-router-dom';
import { Action } from './Action';
import { linkProps } from '../lib/links';
import { scrollToTop } from '../lib/scroll';
import { useActiveSection } from '../hooks/useActiveSection';
import { usePresence } from '../hooks/usePresence';

/**
 * The header was the one component the redesign never reached, and it showed in
 * three separate ways.
 *
 * ACCESSIBILITY. Seventeen controls, zero real elements. The triggers were
 * `<div tabIndex={0} role="button">` with `outline: none` set inline, so the
 * only focusable things in the header were invisible when focused. The panel
 * rendered `position: fixed` at coordinates from `getBoundingClientRect()`,
 * which put it at the end of the DOM, tabbing off a trigger left the nav
 * entirely and the panel it had just opened was unreachable. Every item was a
 * `role="menuitem"` div with a click handler, so nothing had an href.
 *
 * It is now the disclosure-navigation pattern: a real `<button aria-expanded>`
 * per group, and a panel of real links rendered *inside* the trigger's
 * positioning context, so tab order follows reading order for free and the
 * getBoundingClientRect layout read on every render is gone. Escape closes and
 * returns focus to the trigger; tabbing out of the group closes it.
 *
 * STYLE. Two backdrop blurs, an rgba-white border and rgba-white text on a
 * palette that has real tokens for both, a 14px and a 10px radius on a scale
 * whose steps are 6/8/12/16, and a hardcoded `font-family: 'Space Grotesk'`
 * inside a document whose body is already Space Grotesk. Radius is an arch
 * property here, generous on the mark and the primary action, zero everywhere
 * else. So a floating panel gets a hairline and an elevation, not corners.
 *
 * DUPLICATION. An inline `<style>` block redeclared `@keyframes fadeIn`,
 * shadowing global.css's version, which also translates 4px. Whichever loaded
 * last won, so the dropdown's entrance depended on render order.
 */

// Maps section IDs to the nav label they belong to.
//
// `pricing` and `get-started` are deliberately absent. They used to map to the
// "Pricing" nav item: the second one with the comment "close enough", so the
// nav lit up "Pricing" while the reader was looking at the final CTA, and
// clicking the thing that had just lit up navigated them AWAY to /pricing. A
// highlight that does not agree with its own destination is worse than no
// highlight: it tells the reader they are somewhere they are not. The header's
// Pricing link goes to the full pricing page; the landing page's own summary
// section is reached from the hero, which scrolls to it.
const SECTION_TO_NAV: Record<string, string> = {
  'per-tool': 'Product',
  agents: 'Product',
  activity: 'Product',
  keys: 'Product',
  fleet: 'Product',
  pricing: 'Pricing',
  // No 'use-cases' entry: that section was cut from the landing page, and a
  // highlight for a section that no longer exists is worse than none. The three
  // audience pages are still in the header dropdown and are linked from one
  // line under the features grid.
};

interface DropdownItem {
  icon: any;
  title: string;
  description: string;
  href: string;
  /** In-app route rather than an on-page anchor. Off-site is read off the url. */
  route?: boolean;
}

interface NavDropdown {
  label: string;
  items: DropdownItem[];
}

// Order: Product | Use Cases | Catalog, Security, Pricing (standalone) | Resources
const DROPDOWNS: NavDropdown[] = [
  {
    label: 'Product',
    // Features only, each to its own page. Getting started and the docs live
    // under Resources, and Security is a link of its own in the bar.
    items: [
      {
        icon: Shield,
        title: 'Per-tool access',
        description: 'Allow selectRows, deny deleteRows, on one server',
        href: '/mcp-permissions',
        route: true,
      },
      {
        icon: GitBranch,
        title: 'Agent identity',
        description: 'A token per agent you can suspend, revoke or expire',
        href: '/mcp-agent-identity',
        route: true,
      },
      {
        icon: Scroll,
        title: 'Audit logs',
        description: 'Every call recorded, the refused ones too',
        href: '/mcp-audit-logs',
        route: true,
      },
      {
        icon: HardDrives,
        title: 'Self-hosted',
        description: 'Run Statio in your own cloud on Enterprise',
        href: '/self-hosted-mcp-gateway',
        route: true,
      },
      {
        icon: Terminal,
        title: 'mcp-gen',
        description: 'Generate MCP servers from OpenAPI specs',
        href: '/mcp-gen',
        route: true,
      },
      {
        icon: DownloadSimple,
        title: 'Desktop app',
        description: 'Configures your MCP clients and finds unmanaged servers',
        href: '/download',
        route: true,
      },
    ],
  },
  {
    label: 'Use Cases',
    items: [
      {
        icon: Code,
        title: 'For Developers',
        description: 'Connect agents to APIs without scattering keys',
        href: '/use-cases/developers',
        route: true,
      },
      {
        icon: PlugsConnected,
        title: 'For API Providers',
        description: 'Expose your API to the AI ecosystem',
        href: '/use-cases/providers',
        route: true,
      },
      {
        icon: Buildings,
        title: 'For Enterprise',
        description: "Govern your agents' API access",
        href: '/use-cases/enterprise',
        route: true,
      },
    ],
  },
];

const RESOURCES_DROPDOWN: NavDropdown = {
  label: 'Resources',
  items: [
    {
      icon: Rocket,
      title: 'Getting Started',
      description: 'Account, desktop app, first tool',
      href: '/getting-started',
      route: true,
    },
    {
      icon: GraduationCap,
      title: 'Tutorials',
      description: 'Step-by-step MCP how-tos, each one tested',
      href: '/tutorials',
      route: true,
    },
    {
      icon: ListChecks,
      title: 'MCP security checklist',
      description: 'Score your setup in 13 questions',
      href: '/tools/mcp-security-checklist',
      route: true,
    },
    {
      icon: BookOpen,
      title: 'Documentation',
      description: 'Guides, references, and architecture',
      href: 'https://docs.statio.dev/docs/',
    },
    {
      icon: FileText,
      title: 'API Reference',
      description: 'The deploy API, generated from the spec the service serves',
      href: 'https://docs.statio.dev/docs/api',
    },
    { icon: Article, title: 'Blog', description: 'Writing on MCP and AI agent access', href: BLOG_PATH, route: true },
    {
      icon: Code,
      title: 'Statio vs DIY',
      description: 'Build it yourself or use Statio',
      href: '/compare/statio-vs-diy',
      route: true,
    },
  ],
};

const panelId = (label: string, suffix = '') => `nav-panel-${label.toLowerCase().replace(/\s+/g, '-')}${suffix}`;

/** The 36px amber chip in front of every dropdown row. */
function ItemIcon({ icon: Icon }) {
  return (
    <span className="s-item-ico" aria-hidden="true">
      <Icon size={18} weight="bold" />
    </span>
  );
}

function Caret({ size = 14 }) {
  return (
    <span className="s-caret" aria-hidden="true">
      <CaretDown size={size} weight="bold" />
    </span>
  );
}

function NavDropdown({ dropdown, activeDropdown, activeNavLabel, align, onOpen, onHoverIn, onHoverOut, onClose }) {
  const open = activeDropdown === dropdown.label;
  const { mounted, closing } = usePresence(open, 180);
  const id = panelId(dropdown.label);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const closeAndRefocus = () => {
    onClose();
    triggerRef.current?.focus();
  };

  return (
    <div
      className="nav-group"
      onMouseEnter={() => onHoverIn(dropdown.label)}
      onMouseLeave={onHoverOut}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          e.stopPropagation();
          closeAndRefocus();
        }
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) onClose();
      }}
    >
      <Action
        ref={triggerRef}
        className="s-nav-trigger"
        onPress={() => (open ? onClose() : onOpen(dropdown.label))}
        aria-expanded={open}
        aria-controls={id}
        style={activeNavLabel === dropdown.label ? { color: 'var(--ink)' } : undefined}
      >
        {dropdown.label}
        <Caret />
      </Action>
      {mounted && (
        <div
          id={id}
          className={`nav-panel nav-panel-${align}`}
          data-closing={closing || undefined}
          inert={closing || undefined}
        >
          {dropdown.items.map((item) => (
            <Action key={item.title} {...linkProps(item.href, item.route)} onPress={onClose} className="s-item">
              <ItemIcon icon={item.icon} />
              <span>
                <b>{item.title}</b>
                <small>{item.description}</small>
              </span>
            </Action>
          ))}
        </div>
      )}
    </div>
  );
}

function MobileDropdownSection({ dropdown, mobileExpanded, setMobileExpanded, onSelect }) {
  const isExpanded = mobileExpanded === dropdown.label;
  const id = panelId(dropdown.label, '-mobile');
  return (
    <div>
      <Action
        className="m-row"
        onPress={() => setMobileExpanded(isExpanded ? null : dropdown.label)}
        aria-expanded={isExpanded}
        aria-controls={id}
      >
        {dropdown.label}
        <span className="s-caret" aria-hidden="true" style={{ transform: isExpanded ? 'rotate(180deg)' : undefined }}>
          <CaretDown size={18} weight="bold" />
        </span>
      </Action>
      {/* Always rendered so the group can slide open; inert while closed keeps its links out of reach. */}
      <div id={id} className="x-collapse" data-open={isExpanded || undefined} inert={!isExpanded || undefined}>
        <div>
          <div style={{ padding: '8px 0 12px' }}>
            {dropdown.items.map((item) => (
              <Action key={item.title} {...linkProps(item.href, item.route)} onPress={onSelect} className="s-item">
                <ItemIcon icon={item.icon} />
                <span>
                  <b>{item.title}</b>
                  <small>{item.description}</small>
                </span>
              </Action>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeRef = useRef<string | null>(null);
  activeRef.current = activeDropdown;
  const mobile = usePresence(mobileOpen, 180);
  const scrolled = useScrolled();
  const glide = useNavGlide();
  const location = useLocation();
  const activeSection = useActiveSection();
  const activeNavLabel = location.pathname === '/' && activeSection ? SECTION_TO_NAV[activeSection] || null : null;

  const onOpen = useCallback((label: string) => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveDropdown(label);
  }, []);
  // Hover intent: a pointer crossing the bar on its way somewhere waits 70ms before
  // a panel opens; once one is open, moving to the next switches at once.
  const onHoverIn = useCallback((label: string) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (openTimeoutRef.current) clearTimeout(openTimeoutRef.current);
    if (activeRef.current) {
      setActiveDropdown(label);
      return;
    }
    openTimeoutRef.current = setTimeout(() => setActiveDropdown(label), 70);
  }, []);
  const onHoverOut = useCallback(() => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
    closeTimeoutRef.current = setTimeout(() => setActiveDropdown(null), 200);
  }, []);
  const onClose = useCallback(() => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveDropdown(null);
  }, []);
  const closeMobile = useCallback(() => {
    setMobileOpen(false);
    setMobileExpanded(null);
  }, []);
  const dropdownProps = { activeDropdown, activeNavLabel, onOpen, onHoverIn, onHoverOut, onClose };

  return (
    <>
      {/* First focusable node in the document. See .skip-link in global.css. */}
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>
      <header className="s-head site-header" data-scrolled={scrolled || undefined}>
        <div className="s-wrap row">
          {/* A link to `/` that also scrolls up: on the home page it changes no pathname, so ScrollToTop never fires. */}
          <Action to="/" onPress={scrollToTop} aria-label="Statio home" className="s-logo">
            Statio
          </Action>
          {/* Desktop nav is hidden by a media query, not a JS branch, so the prerendered HTML is right at every width. */}
          <nav
            aria-label="Main"
            className="s-nav"
            ref={glide.ref}
            onMouseOver={glide.onMouseOver}
            onMouseLeave={glide.onMouseLeave}
          >
            <span
              className="s-nav-glide"
              aria-hidden="true"
              data-on={glide.on || undefined}
              data-fresh={glide.fresh || undefined}
              style={glide.style}
            />
            {DROPDOWNS.map((dropdown) => (
              <NavDropdown key={dropdown.label} dropdown={dropdown} align="left" {...dropdownProps} />
            ))}
            <Action
              to="/mcp-servers"
              className="s-nav-link"
              aria-current={location.pathname.startsWith('/mcp-servers') ? 'page' : undefined}
            >
              Catalog
            </Action>
            <Action
              to="/security"
              className="s-nav-link"
              aria-current={location.pathname === '/security' ? 'page' : undefined}
            >
              Security
            </Action>
            <Action
              to="/pricing"
              className="s-nav-link"
              aria-current={location.pathname === '/pricing' ? 'page' : undefined}
            >
              Pricing
            </Action>
            <NavDropdown dropdown={RESOURCES_DROPDOWN} align="right" {...dropdownProps} />
            <Action
              to="/download"
              className="s-btn s-btn-sec"
              style={{ height: 38, fontSize: 14 }}
              aria-current={location.pathname.startsWith('/download') ? 'page' : undefined}
            >
              <DownloadSimple size={16} weight="bold" aria-hidden="true" /> Download
            </Action>
            <Action
              href={SIGNUP_URL}
              external
              className="s-btn s-btn-pri"
              style={{ height: 38, fontSize: 14, marginLeft: -14 }}
            >
              Start free
            </Action>
          </nav>
          <Action
            className="s-menu-btn"
            onPress={() => setMobileOpen(!mobileOpen)}
            aria-expanded={mobileOpen}
            aria-controls="mobile-menu"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            <span className="s-menu-ico s-menu-ico-open" aria-hidden="true">
              <List size={24} weight="bold" />
            </span>
            <span className="s-menu-ico s-menu-ico-close" aria-hidden="true">
              <X size={24} weight="bold" />
            </span>
          </Action>
        </div>
      </header>
      {/* State-gated and media-gated: never in the prerendered HTML, and a resize to desktop with it open leaves nothing behind. */}
      {mobile.mounted && (
        <nav
          id="mobile-menu"
          aria-label="Main"
          className="s-mobile"
          data-closing={mobile.closing || undefined}
          inert={mobile.closing || undefined}
        >
          {DROPDOWNS.map((dropdown) => (
            <MobileDropdownSection
              key={dropdown.label}
              dropdown={dropdown}
              mobileExpanded={mobileExpanded}
              setMobileExpanded={setMobileExpanded}
              onSelect={closeMobile}
            />
          ))}
          <Action to="/mcp-servers" onPress={closeMobile} className="m-row">
            Catalog
          </Action>
          <Action to="/security" onPress={closeMobile} className="m-row">
            Security
          </Action>
          <Action to="/pricing" onPress={closeMobile} className="m-row">
            Pricing
          </Action>
          <MobileDropdownSection
            dropdown={RESOURCES_DROPDOWN}
            mobileExpanded={mobileExpanded}
            setMobileExpanded={setMobileExpanded}
            onSelect={closeMobile}
          />
          <div style={{ marginTop: 24, display: 'grid', gap: 10 }}>
            <Action
              href={SIGNUP_URL}
              external
              onPress={closeMobile}
              className="s-btn s-btn-pri"
              style={{ width: '100%', justifyContent: 'center' }}
            >
              Start free
            </Action>
            <Action
              to="/download"
              onPress={closeMobile}
              className="s-btn s-btn-sec"
              style={{ width: '100%', justifyContent: 'center' }}
            >
              <DownloadSimple size={16} weight="bold" aria-hidden="true" /> Download the desktop app
            </Action>
          </div>
        </nav>
      )}
    </>
  );
}

/** True once the page has scrolled under the header; clears only near the top again, so it cannot flicker at the threshold. */
function useScrolled() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    let on = false;
    const update = () => {
      const y = window.scrollY;
      const next = on ? y > 4 : y > 12;
      if (next !== on) setScrolled((on = next));
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, []);
  return scrolled;
}

/** Places the nav's gliding bar under whichever link or trigger the pointer is over. */
function useNavGlide() {
  const ref = useRef<HTMLElement | null>(null);
  const [state, setState] = useState({ on: false, fresh: false, x: 0, y: 0, w: 0 });
  const onMouseOver = useCallback((e) => {
    const item = e.target.closest?.('.s-nav-link, .s-nav-trigger');
    const nav = ref.current;
    if (!item || !nav || !nav.contains(item)) return;
    const n = nav.getBoundingClientRect();
    const r = item.getBoundingClientRect();
    setState((prev) => ({ on: true, fresh: !prev.on, x: r.left - n.left, y: r.bottom - n.top + 6, w: r.width }));
  }, []);
  const onMouseLeave = useCallback(() => setState((prev) => ({ ...prev, on: false, fresh: false })), []);
  const style = { '--glide-x': `${state.x}px`, '--glide-y': `${state.y}px`, '--glide-w': `${state.w}px` };
  return { ref, on: state.on, fresh: state.fresh, style, onMouseOver, onMouseLeave };
}
